import { ShieldCheck, Globe2, Sparkles, Users, Clock, FileCheck2 } from "lucide-react";
import Reveal, { Stagger, StaggerItem } from "../motion/Reveal";
import FlowBackdrop from "../illustrations/FlowBackdrop";

const ITEMS = [
  {
    icon: Globe2,
    stat: "32",
    statLabel: "markets",
    title: "Cross-border by design",
    body:
      "Workflows stress-tested across the US, UK, UAE, Singapore, Canada and Australia. Your residency complexity becomes our compliance work.",
    span: "lg:col-span-7",
  },
  {
    icon: Sparkles,
    stat: "AI",
    statLabel: "guided",
    title: "Decisions, not data dumps",
    body:
      "Models surface what matters: tax-aware rebalancing, FX entry windows, sector shifts — each translated into a clear recommended action.",
    span: "lg:col-span-5",
  },
  {
    icon: ShieldCheck,
    stat: "100%",
    statLabel: "indexed",
    title: "Audit-grade records",
    body:
      "Every transaction, statement and tax certificate indexed and retrievable in seconds — for your CA, your bank, or your own files.",
    span: "lg:col-span-5",
  },
  {
    icon: Users,
    stat: "1:1",
    statLabel: "advisory",
    title: "Human advisors, on call",
    body:
      "SEBI-registered cross-border specialists who understand both your host country and your Indian roots. Same team, every conversation.",
    span: "lg:col-span-7",
  },
];

const MICRO_STATS = [
  { icon: Clock, value: "< 1 day", label: "Advisor response time" },
  { icon: FileCheck2, value: "5 days", label: "Average KYC onboarding" },
  { icon: ShieldCheck, value: "0", label: "Custody of your assets" },
];

export default function WhyUs() {
  return (
    <section
      id="why"
      data-testid="why-us-section"
      className="py-20 lg:py-28 bg-[hsl(var(--zt-teal))] text-[hsl(var(--zt-warm-white))] relative overflow-hidden"
    >
      <div aria-hidden className="absolute inset-0 dot-grid-inverse opacity-30 pointer-events-none" />
      <FlowBackdrop variant="dark" className="opacity-70" />
      <div
        aria-hidden
        className="absolute top-[-10%] right-[-8%] w-[560px] h-[560px] rounded-full opacity-[0.16] blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, #4F63F0 0%, transparent 65%)" }}
      />

      <div className="max-w-[1320px] mx-auto px-6 md:px-12 lg:px-16 relative z-10">
        <Reveal className="max-w-3xl space-y-5 mb-14">
          <div className="flex items-center gap-3">
            <span className="gold-rule" />
            <span className="eyebrow text-[hsl(var(--zt-gold-soft))]">Why Zenstrust</span>
          </div>
          <h2 className="font-display text-[2rem] md:text-[2.6rem] lg:text-[3.1rem] leading-[1.1] text-white">
            Built for the realities of living abroad,{" "}
            <span className="text-[hsl(var(--zt-gold-soft))]">investing at home</span>.
          </h2>
          <p className="text-[1.0625rem] text-white/70 leading-[1.7] max-w-2xl font-body">
            We rebuilt the stack — digital KYC, dedicated advisory, repatriation — around the actual lived
            experience of the global diaspora, not a resident-first product with an NRI checkbox.
          </p>
        </Reveal>

        {/* Feature grid */}
        <Stagger className="grid grid-cols-1 lg:grid-cols-12 gap-4" staggerChildren={0.09}>
          {ITEMS.map(({ icon: Icon, stat, statLabel, title, body, span }, i) => (
            <StaggerItem key={title} className={span}>
              <div
                data-testid={`why-card-${i}`}
                className="group relative h-full overflow-hidden rounded-2xl bg-white/[0.04] border border-white/10 p-7 lg:p-8 transition-all duration-300 hover:bg-white/[0.07] hover:border-[hsl(var(--zt-gold))]/35"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-10 h-10 rounded-lg bg-[hsl(var(--zt-gold))]/15 border border-[hsl(var(--zt-gold))]/25 grid place-items-center transition-colors duration-300 group-hover:bg-[hsl(var(--zt-gold))]/25">
                    <Icon className="text-[hsl(var(--zt-gold-soft))]" size={18} />
                  </div>
                  <div className="text-right">
                    <div className="font-numeric text-[1.75rem] leading-none font-semibold text-white/90">{stat}</div>
                    <div className="font-heading text-[10px] uppercase tracking-[0.12em] text-white/40 font-bold mt-1.5">
                      {statLabel}
                    </div>
                  </div>
                </div>
                <h3 className="font-heading text-[1.0625rem] lg:text-[1.15rem] font-semibold text-white mb-2.5 tracking-tight">
                  {title}
                </h3>
                <p className="text-[13.5px] text-white/65 leading-relaxed font-body">{body}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        {/* Micro-stat strip */}
        <Reveal delay={0.1} className="mt-4">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-6 py-6 grid sm:grid-cols-3 gap-6">
            {MICRO_STATS.map(({ icon: Icon, value, label }) => (
              <div key={label} className="flex items-center gap-3.5">
                <div className="w-9 h-9 rounded-lg bg-[hsl(var(--zt-sage))]/15 grid place-items-center flex-shrink-0">
                  <Icon size={16} className="text-[hsl(var(--zt-sage))]" />
                </div>
                <div>
                  <p className="font-numeric text-[1.05rem] font-semibold text-white">{value}</p>
                  <p className="font-heading text-[11.5px] text-white/50 font-medium mt-0.5">{label}</p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
